import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { FileSearch } from "lucide-react";
import HeroSection from "@/components/HeroSection";
import LazyMount from "@/components/LazyMount";

// Sections sous la ligne de flottaison : code-splitting pour alléger le
// bundle principal (chargées immédiatement après l'hydratation, même contenu).
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const ChronoServiceBlock = lazy(() => import("@/components/ChronoServiceBlock"));
const FormulasSection = lazy(() => import("@/components/FormulasSection"));
const GallerySection = lazy(() => import("@/components/GallerySection"));
const QuoteFormSection = lazy(() => import("@/components/QuoteFormSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const Footer = lazy(() => import("@/components/Footer"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Schema.org structured data — strict, no fake reviews / no aggregateRating.
  // Single canonical entity for the whole site, referenced everywhere via @id.
  const schemaData = {
    "@context": "https://schema.org",
    "@type": ["MovingCompany", "LocalBusiness"],
    "@id": "https://transfastdem.fr/#organization",
    "name": "TRANSFAST DÉMÉNAGEMENT",
    "legalName": "TRANSFAST DEMENAGEMENT",
    "image": "https://transfastdem.fr/assets/transfast/hero-truck.webp",
    "logo": "https://transfastdem.fr/assets/transfast/logo.jpg",
    "url": "https://transfastdem.fr",
    "telephone": "+33182836365",
    "email": "contact@transfastdem.fr",
    "description": "TRANSFAST DÉMÉNAGEMENT, votre expert en déménagement à Paris et en Île-de-France. Devis gratuit en 1h, équipe professionnelle, 4 formules adaptées à vos besoins. Plus de 500 déménagements par an.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "64 Rue Anatole France",
      "addressLocality": "Levallois-Perret",
      "postalCode": "92300",
      "addressRegion": "Île-de-France",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.8914309,
      "longitude": 2.2858325
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "19:00"
      }
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+33182836365",
        "contactType": "customer service",
        "areaServed": "FR",
        "availableLanguage": ["French"]
      },
      {
        "@type": "ContactPoint",
        "telephone": "+33783408585",
        "contactType": "sales",
        "areaServed": "FR",
        "availableLanguage": ["French"],
        "contactOption": "TollFree"
      }
    ],
    "areaServed": [
      {
        "@type": "City",
        "name": "Paris"
      },
      {
        "@type": "State",
        "name": "Île-de-France"
      },
      {
        "@type": "City",
        "name": "Levallois-Perret"
      },
      {
        "@type": "City",
        "name": "Neuilly-sur-Seine"
      },
      {
        "@type": "City",
        "name": "Boulogne-Billancourt"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services de déménagement",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déménagement ECO",
            "description": "Formule économique: protection mobilier, chargement, transport sécurisé, déchargement"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déménagement ECO PLUS",
            "description": "Formule confort: démontage/remontage mobilier inclus"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déménagement STANDARD",
            "description": "Formule tranquillité: mise en carton objets fragiles incluse"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Déménagement LUXE",
            "description": "Formule LUXE : emballage complet et déballage inclus"
          }
        }
      ]
    },
    "sameAs": [
      "https://maps.google.com/?cid=7328410392234194993",
      "https://maps.app.goo.gl/xR8BvgeTKzpJssap9",
      "https://www.facebook.com/TransfastDem/",
      "https://www.instagram.com/transfastdem",
      "https://www.pagesjaunes.fr/pros/63498006"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* WebSite schema — PRIORITY source for the Google SERP "Site Name"
            (https://developers.google.com/search/docs/appearance/site-names).
            Sits next to MovingCompany/LocalBusiness; Google merges multiple
            JSON-LD blocks by @type, no conflict possible. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "TRANSFAST DÉMÉNAGEMENT",
          "alternateName": "Transfast Déménagement",
          "url": "https://transfastdem.fr",
          "publisher": {
            "@id": "https://transfastdem.fr/#organization"
          }
        })}</script>
        {/* Primary Meta Tags */}
        <title>TRANSFAST DÉMÉNAGEMENT | Déménageur Paris & Île-de-France | Devis Gratuit</title>
        <meta name="title" content="TRANSFAST DÉMÉNAGEMENT | Déménageur Paris & Île-de-France | Devis Gratuit" />
        <meta name="description" content="🚚 TRANSFAST DÉMÉNAGEMENT à Levallois-Perret. Déménagement particuliers & entreprises en Île-de-France. ✓ Devis GRATUIT en 1h ✓ Note 4.9/5 Google ✓ 500+ déménagements/an. ☎ 01 82 83 63 65" />
        <meta name="keywords" content="déménagement paris, déménageur île-de-france, déménagement levallois-perret, entreprise déménagement, devis déménagement gratuit, déménageur pas cher paris, déménagement 92, transfast demenagement, société déménagement paris" />
        <meta name="author" content="TRANSFAST DÉMÉNAGEMENT" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://transfastdem.fr/" />
        
        {/* Viewport for mobile optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://transfastdem.fr/" />
        <meta property="og:title" content="TRANSFAST DÉMÉNAGEMENT | Déménageur Paris & Île-de-France" />
        <meta property="og:description" content="Votre expert en déménagement à Paris et Île-de-France. Devis gratuit en 1h, équipe professionnelle. Note 4.9/5 sur Google. Appelez le 01 82 83 63 65" />
        <meta property="og:image" content="https://transfastdem.fr/assets/transfast/og-home-truck-exact.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="TRANSFAST DÉMÉNAGEMENT" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://transfastdem.fr/" />
        <meta name="twitter:title" content="TRANSFAST DÉMÉNAGEMENT | Déménageur Paris & Île-de-France" />
        <meta name="twitter:description" content="Votre expert en déménagement à Paris et Île-de-France. Devis gratuit en 1h. Note 4.9/5 sur Google." />
        <meta name="twitter:image" content="https://transfastdem.fr/assets/transfast/og-home-truck-exact.jpg" />

        {/* Geographic Meta Tags */}
        <meta name="geo.region" content="FR-IDF" />
        <meta name="geo.placename" content="Levallois-Perret" />
        <meta name="geo.position" content="48.8914309;2.2858325" />
        <meta name="ICBM" content="48.8914309, 2.2858325" />
        
        {/* Business Info */}
        <meta name="contact" content="contact@transfastdem.fr" />
        <meta name="reply-to" content="contact@transfastdem.fr" />
        
        {/* Theme Color */}
        <meta name="theme-color" content="#004E98" />
        <meta name="msapplication-navbutton-color" content="#004E98" />
        <meta name="apple-mobile-web-app-status-bar-style" content="#004E98" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        
        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://transfastdem.fr" />
      </Helmet>
      
      <Header isScrolled={isScrolled} />
      <main>
        <HeroSection />
        <LazyMount minHeight={1600} idleDelay={1400}>
          <Suspense fallback={null}>
            <ServicesSection />
            <ChronoServiceBlock />
            <FormulasSection />
          </Suspense>
        </LazyMount>
        {/* SEO inline banner — internal link to /comparatif-devis with
            an exact-match anchor for Surfer (keyword "devis comparatif
            déménagement"). Lightweight, no extra component, no API call. */}
        <section className="py-8 bg-gradient-to-r from-[#FFF7ED] to-[#FFEBD5] border-y border-orange-100" data-testid="home-comparatif-banner">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-center md:text-left">
              <div className="hidden sm:flex w-11 h-11 rounded-full bg-[#FF6B00]/10 items-center justify-center flex-shrink-0">
                <FileSearch className="w-5 h-5 text-[#FF6B00]" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-bold text-[#004E98] leading-tight">
                  Vous avez déjà un devis d'un autre déménageur ?
                </p>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Recevez un <strong>devis comparatif déménagement</strong> détaillé TRANSFAST gratuit sous 1 h, sans engagement.
                </p>
              </div>
            </div>
            <Link
              to="/comparatif-devis"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B00] hover:bg-[#e55f00] text-white font-bold text-sm whitespace-nowrap transition-colors"
              data-testid="home-comparatif-cta"
            >
              Comparer mon devis de déménagement →
            </Link>
          </div>
        </section>
        <LazyMount minHeight={520} idleDelay={2200}>
          <Suspense fallback={null}><GallerySection /></Suspense>
        </LazyMount>
        <LazyMount minHeight={900} idleDelay={2600} placeholderId="devis">
          <Suspense fallback={null}><QuoteFormSection /></Suspense>
        </LazyMount>
        <LazyMount minHeight={520} idleDelay={3000}>
          <Suspense fallback={null}><TestimonialsSection /></Suspense>
        </LazyMount>
        <LazyMount minHeight={520} idleDelay={3400}>
          <Suspense fallback={null}><ContactSection /></Suspense>
        </LazyMount>
      </main>
      <LazyMount minHeight={400} idleDelay={3800}>
        <Suspense fallback={null}>
          <Footer />
          <WhatsAppButton />
        </Suspense>
      </LazyMount>
    </div>
  );
}
